/*
 * Vedistry Holistic Health Assessment — engine
 *
 * Flow:  intro → concern → [subconcern] → question×N → [screening] → details → result
 *                                                                        └→ exit (under 18)
 *
 * Per the build guide, no answer data is written to localStorage or
 * sessionStorage. State lives in memory for the life of the page; a cold load
 * on a deep-linked step resets to the intro rather than restoring answers.
 */
(function () {
  'use strict';

  var CONTENT = window.VedistryAssessmentContent;
  if (!CONTENT) return;

  // Bump when this file changes. `VedistryAssessment.build` in the console
  // tells you which copy the storefront is actually running.
  var BUILD = 'report-download-2026-10-06';

  /* ------------------------------------------------------------------ *
   * Small helpers
   * ------------------------------------------------------------------ */

  function el(tag, className, text) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (text != null) node.textContent = text;
    return node;
  }

  function frag() {
    return document.createDocumentFragment();
  }

  /**
   * Digits only, with an Indian country code or trunk zero removed. Indian
   * numbers arrive as "+91 98765 43210" or "098765 43210" far too often to
   * reject outright — both are the same number as "9876543210". The prefix
   * has to go before any truncation, never after, or the tail of a pasted
   * number survives and the lead is unreachable.
   *
   * This deliberately does NOT cut the result to length: an eleven-digit entry
   * is a typo, and silently trimming it would turn it into a plausible but
   * wrong number. Only the live field truncates, as a typing cap.
   */
  /**
   * Digits of the national number, with a trunk zero or a repeated dial code
   * peeled off. Contact lists carry the same number as 0…, 91…, +91… and 091…,
   * and people paste the dial code into the field even when it is already
   * chosen in the dropdown.
   *
   * Every peel is guarded on the number being longer than expected, so a
   * genuine number that happens to begin with the dial code's digits — 9198…
   * in India — is left alone. Nothing is ever cut to fit: an over-long entry
   * is a typo, and trimming it would produce a plausible but wrong number.
   */
  function mobileDigits(raw, dialCode, expected) {
    var digits = String(raw == null ? '' : raw).replace(/\D/g, '');
    var cc = String(dialCode == null ? '' : dialCode).replace(/\D/g, '');
    expected = expected || 10;

    if (digits.length > expected) digits = digits.replace(/^0+/, '');
    if (cc && digits.length > expected && digits.slice(0, cc.length) === cc) {
      digits = digits.slice(cc.length);
    }
    if (digits.length > expected) digits = digits.replace(/^0+/, '');
    return digits;
  }

  function countryFor(code) {
    var list = CONTENT.countries || [];
    for (var i = 0; i < list.length; i++) {
      if (list[i].code === code) return list[i];
    }
    return list[0] || { name: 'India', code: '+91', digits: 10 };
  }

  /* ------------------------------------------------------------------ *
   * Scoring
   * ------------------------------------------------------------------ */

  /**
   * Sheet 4: score = total ÷ maximum × 100, banded on the *unrounded* value.
   * The `<= 60` edge matters — on a 15-max module a total of 9 is exactly
   * 60.0% and must band Moderate, not Severe.
   */
  function bandFor(pct) {
    for (var i = 0; i < CONTENT.bands.length; i++) {
      if (pct <= CONTENT.bands[i].max) return CONTENT.bands[i];
    }
    return CONTENT.bands[CONTENT.bands.length - 1];
  }

  function score(module, answers) {
    var total = 0;
    module.questions.forEach(function (question) {
      var value = answers[question.id];
      if (typeof value === 'number') total += value;
    });
    var pct = (total / module.max) * 100;
    return { total: total, max: module.max, pct: pct, band: bandFor(pct) };
  }

  function redFlags(module, answers, result, screening) {
    var hits = [];

    CONTENT.redFlags.forEach(function (rule) {
      if (rule.type === 'question') {
        if (answers[rule.id] === rule.eq) hits.push(rule.meaning);
        return;
      }
      if (rule.module !== '*' && rule.module !== module.id) return;
      if (rule.gt != null && result.pct > rule.gt) hits.push(rule.meaning);
      if (rule.gte != null && result.pct >= rule.gte) hits.push(rule.meaning);
    });

    if (screening === 'yes') hits.push('Pregnant or breastfeeding');
    return hits;
  }

  /* ------------------------------------------------------------------ *
   * Instance
   * ------------------------------------------------------------------ */

  function Assessment(root) {
    this.root = root;
    this.stage = root.querySelector('[data-va-stage]');
    this.intro = root.querySelector('[data-va-intro]');
    this.settings = this.readSettings();
    this.copy = Object.assign({}, CONTENT.copy, this.settings.copy || {});
    this.reset();
    this.bind();
  }

  Assessment.prototype.readSettings = function () {
    var node = this.root.querySelector('[data-va-settings]');
    if (!node) {
      console.error('[vedistry] settings block missing — section markup is stale');
      return {};
    }

    var raw = node.textContent;
    try {
      return JSON.parse(raw) || {};
    } catch (error) {
      // Silently falling back to {} here hides a broken settings block: the
      // quiz still runs, but every setting — product mapping, images, the sheet
      // endpoint — is quietly lost. A Liquid error rendered into the JSON is
      // the usual cause, so surface the offending text.
      var liquidError = raw.match(/Liquid error[^,\n]*/);
      console.error(
        '[vedistry] settings JSON is invalid, so ALL section settings were ' +
          'ignored (products, images, sheet endpoint). ' +
          (liquidError
            ? 'Cause: ' + liquidError[0]
            : 'Parse error: ' + error.message),
        raw
      );
      return {};
    }
  };

  Assessment.prototype.reset = function () {
    this.state = 'intro';
    this.concern = null;
    this.subConcern = null;
    this.module = null;
    this.answers = {};
    this.index = 0;
    this.screening = null;
    this.details = null;
    this.result = null;
    this.flags = [];
    this.completed = false;
    this.lead = null;
  };

  /* --- analytics ---------------------------------------------------- */

  /**
   * Guide: full payload on a DOM event for first-party use; ad platforms get a
   * category-level signal only — no score, no band, no per-question answers.
   */
  Assessment.prototype.track = function (event, payload) {
    document.dispatchEvent(
      new CustomEvent('vedistry:assessment', {
        detail: Object.assign({ event: event }, payload || {})
      })
    );

    if (!this.settings.sendAdSignals) return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: event,
      concern: (payload && payload.concern) || this.concernTitle() || null
    });
  };

  Assessment.prototype.concernTitle = function () {
    return this.concern ? this.concern.title : null;
  };

  /* --- navigation --------------------------------------------------- */

  Assessment.prototype.go = function (state, options) {
    var push = !options || options.push !== false;
    this.state = state;
    if (push && window.history && window.history.pushState) {
      window.history.pushState({ vedistry: state, index: this.index }, '');
    }
    this.render();
  };

  Assessment.prototype.bind = function () {
    var self = this;

    this.root.addEventListener('click', function (event) {
      var trigger = event.target.closest('[data-va-action]');
      if (!trigger || !self.root.contains(trigger)) return;
      event.preventDefault();
      self.action(trigger.getAttribute('data-va-action'), trigger);
    });

    window.addEventListener('popstate', function (event) {
      var state = event.state && event.state.vedistry;
      if (!state) return;
      // Walking back through questions restores the answered question, not the
      // start of the module — the guide requires Back to preserve answers.
      if (state === 'question' && typeof event.state.index === 'number') {
        self.index = event.state.index;
      }
      self.state = state;
      self.render();
    });

    // quiz_abandoned: fired once, on the way out, if they left mid-question.
    window.addEventListener('pagehide', function () {
      if (self.completed || self.state !== 'question') return;
      var question = self.module && self.module.questions[self.index];
      self.track('quiz_abandoned', {
        last_question_id: question ? question.id : null
      });
    });
  };

  Assessment.prototype.action = function (name, trigger) {
    switch (name) {
      case 'start':
        this.track('quiz_start');
        this.go('concern');
        break;
      case 'back':
        window.history.back();
        break;
      case 'retake':
        this.track('quiz_result_cta_click', { cta_type: 'retake' });
        this.reset();
        this.track('quiz_start');
        this.go('concern');
        break;
      case 'share':
        this.track('quiz_result_cta_click', { cta_type: 'share' });
        window.open(
          'https://wa.me/?text=' + encodeURIComponent(this.copy.shareText),
          '_blank',
          'noopener'
        );
        break;
      case 'consult':
        this.track('quiz_result_cta_click', { cta_type: 'consultation' });
        if (this.settings.consultationUrl) {
          window.location.href = this.settings.consultationUrl;
        }
        break;
      case 'add-to-cart':
        this.addToCart(trigger);
        break;
      case 'report':
        this.downloadReport(trigger);
        break;
    }
  };

  /* --- rendering ---------------------------------------------------- */

  Assessment.prototype.render = function () {
    var showIntro = this.state === 'intro';
    // Only the result screen widens to two columns; the flow itself stays in
    // the narrow mobile-first column on every viewport.
    this.root.classList.toggle('is-result', this.state === 'result');
    if (this.intro) this.intro.hidden = !showIntro;
    this.stage.hidden = showIntro;
    this.stage.innerHTML = '';
    if (showIntro) return;

    var view = {
      concern: this.renderConcern,
      subconcern: this.renderSubConcern,
      question: this.renderQuestion,
      screening: this.renderScreening,
      details: this.renderDetails,
      exit: this.renderExit,
      result: this.renderResult
    }[this.state];

    if (view) this.stage.appendChild(view.call(this));
    this.focusHeading();
  };

  Assessment.prototype.focusHeading = function () {
    var heading = this.stage.querySelector('[data-va-heading]');
    if (!heading) return;
    heading.setAttribute('tabindex', '-1');
    heading.focus({ preventScroll: true });
    this.stage.scrollIntoView({ block: 'nearest' });
  };

  /** Top row: back link, step label, progress bar. */
  Assessment.prototype.renderProgress = function (label, ratio, withBack) {
    var wrap = el('div', 'va-top');
    var row = el('div', 'va-top-row' + (withBack ? '' : ' va-top-row--start'));

    if (withBack) {
      var back = el('button', 'va-back', '‹ ' + this.copy.backLabel);
      back.type = 'button';
      back.setAttribute('data-va-action', 'back');
      row.appendChild(back);
    }

    var status = el('span', 'va-step-label', label);
    status.setAttribute('aria-live', 'polite');
    row.appendChild(status);
    wrap.appendChild(row);

    var track = el('div', 'va-progress');
    track.setAttribute('role', 'presentation');
    var fill = el('span', 'va-progress-fill');
    fill.style.width = Math.max(0, Math.min(1, ratio)) * 100 + '%';
    track.appendChild(fill);
    wrap.appendChild(track);

    return wrap;
  };

  Assessment.prototype.renderConcern = function () {
    var self = this;
    var out = frag();
    out.appendChild(this.renderProgress('Step 1 of 3', 1 / 3, false));

    var heading = el('h2', 'va-headline', this.copy.concernHeadline);
    heading.setAttribute('data-va-heading', '');
    out.appendChild(heading);
    out.appendChild(el('p', 'va-subline', this.copy.concernSubline));

    var grid = el('div', 'va-grid');
    CONTENT.concerns.forEach(function (concern) {
      var card = el('button', 'va-concern');
      card.type = 'button';
      card.appendChild(self.iconFor(concern.id));
      card.appendChild(el('span', 'va-concern-title', concern.title));
      card.appendChild(el('span', 'va-concern-sub', concern.sub));
      card.addEventListener('click', function () {
        self.selectConcern(concern);
      });
      grid.appendChild(card);
    });
    out.appendChild(grid);
    return out;
  };

  Assessment.prototype.iconFor = function (id) {
    var wrap = el('span', 'va-icon');
    wrap.setAttribute('aria-hidden', 'true');
    var custom = this.settings.icons && this.settings.icons[id];
    if (custom) {
      var img = el('img');
      img.src = custom;
      img.alt = '';
      img.loading = 'lazy';
      wrap.appendChild(img);
    }
    return wrap;
  };

  Assessment.prototype.selectConcern = function (concern) {
    this.concern = concern;
    this.subConcern = null;
    if (concern.children) {
      this.track('quiz_concern_selected', {
        concern: concern.title,
        sub_concern: null
      });
      this.go('subconcern');
      return;
    }
    this.module = CONTENT.modules[concern.module];
    this.index = 0;
    this.track('quiz_concern_selected', {
      concern: concern.title,
      sub_concern: null
    });
    this.go('question');
  };

  Assessment.prototype.renderSubConcern = function () {
    var self = this;
    var out = frag();
    out.appendChild(this.renderProgress('Step 1 of 3', 1 / 3, true));

    var heading = el('h2', 'va-headline', this.copy.subConcernHeadline);
    heading.setAttribute('data-va-heading', '');
    out.appendChild(heading);

    var chip = el('span', 'va-chip', this.concern.title);
    out.appendChild(chip);

    var list = el('div', 'va-options');
    this.concern.children.forEach(function (child) {
      var option = el('button', 'va-option');
      option.type = 'button';
      option.appendChild(el('span', 'va-option-label', child.title));
      option.addEventListener('click', function () {
        self.module = CONTENT.modules[child.id];
        self.subConcern = child.title;
        self.index = 0;
        self.track('quiz_concern_selected', {
          concern: self.concern.title,
          sub_concern: child.title
        });
        self.go('question');
      });
      list.appendChild(option);
    });
    out.appendChild(list);
    return out;
  };

  Assessment.prototype.renderQuestion = function () {
    var self = this;
    var module = this.module;
    var question = module.questions[this.index];
    var number = this.index + 1;
    var count = module.questions.length;

    var out = frag();
    out.appendChild(
      this.renderProgress('Question ' + number + ' of ' + count, number / count, true)
    );
    out.appendChild(el('span', 'va-chip', module.chip));

    // A real fieldset/legend with radio inputs, per the guide's a11y notes.
    var fieldset = el('fieldset', 'va-fieldset');
    var legend = el('legend', 'va-headline', question.text);
    legend.setAttribute('data-va-heading', '');
    fieldset.appendChild(legend);

    var list = el('div', 'va-options');
    question.options.forEach(function (label, optionScore) {
      var id = 'va-' + question.id + '-' + optionScore;
      var option = el('label', 'va-option');
      option.setAttribute('for', id);

      var input = el('input');
      input.type = 'radio';
      input.name = question.id;
      input.id = id;
      input.value = String(optionScore);
      input.className = 'va-option-input';
      if (self.answers[question.id] === optionScore) {
        input.checked = true;
        option.classList.add('is-selected');
      }

      input.addEventListener('change', function () {
        self.answer(question, optionScore, number);
      });

      option.appendChild(input);
      option.appendChild(el('span', 'va-option-label', label));
      option.appendChild(self.tick());
      list.appendChild(option);
    });

    fieldset.appendChild(list);
    out.appendChild(fieldset);
    out.appendChild(el('p', 'va-hint', this.copy.questionHint));
    return out;
  };

  Assessment.prototype.tick = function () {
    var span = el('span', 'va-tick');
    span.setAttribute('aria-hidden', 'true');
    span.innerHTML =
      '<svg viewBox="0 0 20 20" width="20" height="20" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"><path d="M4 10.5l4 4 8-9"/></svg>';
    return span;
  };

  /** Tap selects and advances — there is deliberately no Next button. */
  Assessment.prototype.answer = function (question, optionScore, number) {
    var self = this;
    this.answers[question.id] = optionScore;
    this.track('quiz_question_answered', {
      question_id: question.id,
      option_index: optionScore,
      question_number: number
    });

    var selected = this.stage.querySelector(
      'input[name="' + question.id + '"]:checked'
    );
    if (selected) selected.closest('.va-option').classList.add('is-selected');

    var delay = window.matchMedia('(prefers-reduced-motion: reduce)').matches
      ? 0
      : 220;

    window.setTimeout(function () {
      if (self.index < self.module.questions.length - 1) {
        self.index += 1;
        self.go('question');
      } else if (self.settings.screeningEnabled) {
        self.go('screening');
      } else {
        self.go('details');
      }
    }, delay);
  };

  Assessment.prototype.renderScreening = function () {
    var self = this;
    var out = frag();
    out.appendChild(this.renderProgress('Step 2 of 3', 2 / 3, true));

    var heading = el('h2', 'va-headline', this.copy.screeningQuestion);
    heading.setAttribute('data-va-heading', '');
    out.appendChild(heading);
    out.appendChild(el('p', 'va-subline', this.copy.screeningHeadline));

    var list = el('div', 'va-options');
    [
      { value: 'yes', label: this.copy.screeningYes },
      { value: 'no', label: this.copy.screeningNo },
      { value: 'skip', label: this.copy.screeningSkip }
    ].forEach(function (choice) {
      var option = el('button', 'va-option');
      option.type = 'button';
      option.appendChild(el('span', 'va-option-label', choice.label));
      option.addEventListener('click', function () {
        self.screening = choice.value;
        self.go('details');
      });
      list.appendChild(option);
    });
    out.appendChild(list);
    return out;
  };

  Assessment.prototype.renderDetails = function () {
    var self = this;
    var out = frag();
    out.appendChild(this.renderProgress('Step 3 of 3', 1, true));

    var heading = el('h2', 'va-headline', this.copy.detailsHeadline);
    heading.setAttribute('data-va-heading', '');
    out.appendChild(heading);
    out.appendChild(el('p', 'va-subline', this.copy.detailsSubline));

    var form = el('form', 'va-form');
    form.noValidate = true;

    var fields = [
      { name: 'name', label: 'Full name', type: 'text', placeholder: 'Your name', autocomplete: 'name', required: true, maxLength: 60 },
      { name: 'age', label: 'Age', type: 'number', placeholder: 'Age in years', autocomplete: 'off', required: true },
      { name: 'mobile', label: 'Mobile number', type: 'tel', placeholder: '10-digit mobile', autocomplete: 'tel', required: true },
      { name: 'email', label: 'Email (optional)', type: 'email', placeholder: 'name@email.com', autocomplete: 'email', required: false, maxLength: 120 }
    ];

    fields.forEach(function (field) {
      var wrap = el('div', 'va-field');
      var id = 'va-field-' + field.name;

      var label = el('label', 'va-label', field.label);
      label.setAttribute('for', id);
      wrap.appendChild(label);

      var input = el('input');
      input.type = field.type;
      input.id = id;
      input.name = field.name;
      input.placeholder = field.placeholder;
      input.autocomplete = field.autocomplete;
      if (field.required) input.required = true;
      if (field.maxLength) input.maxLength = field.maxLength;

      if (field.name === 'mobile') {
        input.inputMode = 'numeric';
      }

      if (field.name === 'age') {
        input.inputMode = 'numeric';
        input.min = '1';
        input.max = '100';
        // maxlength does not apply to type=number, so cap the digits directly.
        input.addEventListener('input', function () {
          var digits = input.value.replace(/\D/g, '').slice(0, 3);
          if (input.value !== digits) input.value = digits;
        });
      }
      input.setAttribute('aria-describedby', id + '-error');

      if (field.name === 'mobile') {
        // Dial code and number sit on one row so the number field only ever
        // holds the national digits.
        var row = el('div', 'va-tel');

        var dial = el('select', 'va-dial');
        dial.id = 'va-field-country';
        dial.name = 'country';
        dial.setAttribute('aria-label', self.copy.countryLabel);

        (CONTENT.countries || []).forEach(function (country) {
          var option = el('option', '', country.name + ' ' + country.code);
          option.value = country.code;
          dial.appendChild(option);
        });

        // Deliberately no maxlength on the number: the browser truncates a
        // pasted "+91…" to ten characters before this handler could strip the
        // dial code, and the wrong digits survive.
        //
        // Typing past the limit is capped, so the field simply stops — the
        // familiar behaviour. A paste is never cut, because trimming an
        // over-long paste would produce a plausible but wrong number instead
        // of an error. Anything left over the limit is rejected on submit.
        var pasted = false;
        input.addEventListener('paste', function () {
          pasted = true;
        });

        var clean = function () {
          var country = countryFor(dial.value);
          var cleaned = mobileDigits(input.value, country.code, country.digits);
          if (!pasted) cleaned = cleaned.slice(0, country.digits);
          pasted = false;
          if (input.value !== cleaned) input.value = cleaned;
          input.placeholder = country.digits + '-digit mobile';
        };
        input.addEventListener('input', clean);
        dial.addEventListener('change', clean);

        row.appendChild(dial);
        row.appendChild(input);
        wrap.appendChild(row);
      } else {
        wrap.appendChild(input);
      }

      var error = el('p', 'va-error');
      error.id = id + '-error';
      error.hidden = true;
      wrap.appendChild(error);

      form.appendChild(wrap);
    });

    var consentWrap = el('div', 'va-consent');
    var consent = el('input');
    consent.type = 'checkbox';
    consent.id = 'va-consent';
    consent.name = 'consent';
    consent.required = true;
    var consentLabel = el('label', '', this.copy.detailsConsent);
    consentLabel.setAttribute('for', 'va-consent');
    consentWrap.appendChild(consent);
    consentWrap.appendChild(consentLabel);
    form.appendChild(consentWrap);

    var consentError = el('p', 'va-error');
    consentError.hidden = true;
    form.appendChild(consentError);

    var submit = el('button', 'va-button va-button--primary', this.copy.detailsButton);
    submit.type = 'submit';
    form.appendChild(submit);

    form.addEventListener('submit', function (event) {
      event.preventDefault();
      self.submitDetails(form, consent, consentError);
    });

    out.appendChild(form);
    return out;
  };

  Assessment.prototype.submitDetails = function (form, consent, consentError) {
    var country = countryFor(form.elements.country ? form.elements.country.value : null);
    var values = {
      name: form.elements.name.value.trim(),
      age: parseInt(form.elements.age.value, 10),
      mobile: mobileDigits(form.elements.mobile.value, country.code, country.digits),
      email: form.elements.email.value.trim()
    };

    var errors = {};
    if (values.name.length < 2 || values.name.length > 60) {
      errors.name = 'Please enter your name, 2 to 60 characters.';
    }
    if (!values.age || values.age < 1 || values.age > 100) {
      errors.age = 'Please enter an age between 1 and 100.';
    }
    // Exactly the expected number of digits, or the form does not submit.
    if (values.mobile.length !== country.digits || !/^[0-9]+$/.test(values.mobile)) {
      errors.mobile =
        'Please enter a ' + country.digits + '-digit mobile number' +
        (values.mobile.length
          ? ' — you have entered ' + values.mobile.length + '.'
          : '.');
    }
    if (values.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(values.email)) {
      errors.email = 'Please enter a valid email address.';
    }

    var firstInvalid = null;
    ['name', 'age', 'mobile', 'email'].forEach(function (name) {
      var input = form.elements[name];
      var error = document.getElementById('va-field-' + name + '-error');
      var message = errors[name];
      input.setAttribute('aria-invalid', message ? 'true' : 'false');
      error.textContent = message || '';
      error.hidden = !message;
      if (message && !firstInvalid) firstInvalid = input;
    });

    // Submit stays blocked until consent is ticked, per the guide.
    var consentMissing = !consent.checked;
    consentError.textContent = consentMissing
      ? 'Please tick the box so we can send you your assessment.'
      : '';
    consentError.hidden = !consentMissing;
    if (consentMissing && !firstInvalid) firstInvalid = consent;

    if (firstInvalid) {
      firstInvalid.focus();
      return;
    }

    values.countryCode = country.code;
    values.country = country.name;
    values.mobileFull = country.code + values.mobile;
    this.details = values;

    // Under 18 exits the flow with a message and no result.
    if (values.age < 18) {
      this.go('exit');
      return;
    }

    this.finish();
  };

  Assessment.prototype.finish = function () {
    this.result = score(this.module, this.answers);
    this.flags = redFlags(this.module, this.answers, this.result, this.screening);
    this.completed = true;

    this.track('quiz_completed', {
      module: this.module.id,
      band: this.result.band.id,
      flagged: this.flags.length > 0
    });

    try {
      this.submitLead();
    } catch (error) {
      console.error('[vedistry] lead submission failed', error);
    }
    this.go('result');
  };

  /**
   * Everything the run produced, in one flat-ish object: the form fields, every
   * selection the customer made, and the result they were shown. This is what
   * lands in the Google Sheet and what the `vedistry:assessment:lead` event
   * carries, so any other destination gets the same shape.
   */
  Assessment.prototype.leadPayload = function () {
    var self = this;
    var content = this.module.bands[this.result.band.id];

    var answers = this.module.questions.map(function (question, i) {
      var value = self.answers[question.id];
      return {
        number: i + 1,
        id: question.id,
        question: question.text,
        answer: typeof value === 'number' ? question.options[value] : null,
        score: typeof value === 'number' ? value : null
      };
    });

    var supporting = content.plan.supporting.map(function (product) {
      return product.name + ' — ' + product.dose + (product.note ? ' (' + product.note + ')' : '');
    });

    // Structured products for the PDF. Links must be absolute — a PDF has no
    // page to resolve "/products/…" against.
    var origin = window.location.origin;
    var mapped = this.settings.products || {};

    function absolute(url) {
      if (!url) return '';
      return url.charAt(0) === '/' ? origin + url : url;
    }

    function describe(product, role) {
      var hit = mapped[product.key] || {};
      return {
        key: product.key,
        name: product.name,
        dose: product.dose,
        note: product.note || '',
        role: role,
        url: absolute(hit.url),
        image: hit.image || '',
        price: hit.price || ''
      };
    }

    var products = [describe(content.plan.primary, 'primary')].concat(
      content.plan.supporting.map(function (product) {
        return describe(product, 'supporting');
      })
    );

    return {
      version: CONTENT.version,
      submission_id:
        String(Date.now()) + '-' + Math.random().toString(36).slice(2, 10),
      submitted_at: new Date().toISOString(),
      page_url: window.location.href,

      name: this.details.name,
      age: this.details.age,
      country: this.details.country,
      country_code: this.details.countryCode,
      mobile: this.details.mobile,
      mobile_full: this.details.mobileFull,
      email: this.details.email || '',
      consent: true,

      concern: this.concernTitle(),
      sub_concern: this.subConcern || '',
      module: this.module.id,
      module_label: this.module.label,

      total: this.result.total,
      max: this.result.max,
      percentage: Math.round(this.result.pct * 10) / 10,
      band: this.result.band.label,

      flagged: this.flags.length > 0,
      flag_reasons: this.flags.join('; '),
      screening: this.screening || '',

      answers: answers,
      meaning: content.meaning,

      // The guide splits each bullet by surface: the screen shows the first
      // few, the plan carries the full set. The PDF is the plan, so it gets
      // all three of each rather than the two rendered on the result page.
      diet_dos: this.module.diet.dos,
      diet_donts: this.module.diet.donts,
      habit_dos: this.module.habits.dos,
      habit_donts: this.module.habits.donts,
      // Name plus photo, so the PDF's yoga cards match the page. Shopify's
      // CDN URLs are already absolute.
      yoga: this.module.yoga.map(function (key) {
        return {
          name: CONTENT.yoga[key] || key,
          image: (self.settings.yoga || {})[key] || ''
        };
      }),

      plan_primary: content.plan.primary.name + ' — ' + content.plan.primary.dose,
      plan_supporting: supporting.join(' | '),
      plan_note: content.planNote || '',
      consultation: content.consultation,

      // Everything the PDF needs to look like the page the customer just saw.
      products: products,
      consultation_url: absolute(this.settings.consultationUrl),
      store_url: origin,
      copy: {
        greeting: this.copy.resultGreeting,
        subline: this.copy.resultSubline,
        planHeading: this.copy.planHeading,
        tipsHeading: this.copy.tipsHeading,
        dietLabel: this.copy.dietLabel,
        habitsLabel: this.copy.habitsLabel,
        yogaHeading: this.copy.yogaHeading,
        consultHeadline: this.copy.consultHeadline,
        consultBody: this.copy.consultBody,
        consultButton: this.copy.consultButton,
        aboutHeadline: this.copy.aboutHeadline,
        aboutBody: this.copy.aboutBody,
        trustBar: this.copy.trustBar,
        disclaimer: this.copy.resultDisclaimer
      }
    };
  };

  /**
   * Fire-and-forget to the Apps Script endpoint. `sendBeacon` survives the
   * customer navigating away mid-request, and a text/plain body keeps it a
   * simple request so no CORS preflight is needed — Apps Script web apps do not
   * answer preflights. Nothing here can read the response, so a failed write is
   * silent by design; the DOM event always fires regardless.
   */
  Assessment.prototype.submitLead = function () {
    var payload = this.leadPayload();
    // Kept so "Download report" renders exactly what was logged.
    this.lead = payload;

    document.dispatchEvent(
      new CustomEvent('vedistry:assessment:lead', { detail: payload })
    );

    // Set window.VEDISTRY_DEBUG = true in the console before completing a run
    // to trace this. Everything below is fire-and-forget, so without it a
    // misconfigured endpoint looks identical to a working one.
    var debug = window.VEDISTRY_DEBUG;
    var endpoint = (this.settings.sheetEndpoint || '').trim();

    if (!endpoint) {
      if (debug) console.warn('[vedistry] no sheet endpoint configured', payload);
      return;
    }
    if (debug) console.info('[vedistry] posting to', endpoint, payload);
    if (endpoint.slice(-5) !== '/exec') {
      console.warn(
        '[vedistry] the Apps Script URL should end in /exec — got: ' + endpoint
      );
    }

    // Build the transport body from a copy: the secret must not end up on the
    // object already handed to `vedistry:assessment:lead` listeners.
    var envelope = Object.assign({}, payload);
    if (this.settings.sheetSecret) envelope.secret = this.settings.sheetSecret;

    var body = JSON.stringify(envelope);
    var type = 'text/plain;charset=utf-8';

    function beacon(why) {
      // Last resort: fires reliably but the response cannot be read, so a
      // failure here is genuinely invisible.
      if (debug) console.warn('[vedistry] falling back to sendBeacon —', why);
      try {
        if (navigator.sendBeacon) {
          navigator.sendBeacon(endpoint, new Blob([body], { type: type }));
        }
      } catch (error) {
        console.warn('[vedistry] sendBeacon failed', error);
      }
    }

    if (!window.fetch) {
      beacon('fetch unavailable');
      return;
    }

    // Try a readable request first. text/plain keeps it preflight-free, which
    // Apps Script needs, and its response carries Access-Control-Allow-Origin,
    // so the script's own ok/error usually comes back. When it does not, the
    // request may still have been written — submission_id makes the retry safe.
    fetch(endpoint, {
      method: 'POST',
      mode: 'cors',
      keepalive: true,
      headers: { 'Content-Type': type },
      body: body
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (result) {
        if (result && result.ok) {
          if (debug) console.info('[vedistry] sheet accepted the row', result);
          return;
        }
        // It reached the script and was refused, so do not retry — say why.
        console.warn(
          '[vedistry] the sheet refused this row: ' +
            ((result && result.error) || 'unknown') +
            '. Check the Shared secret setting against SHARED_SECRET, and that ' +
            'the deployment is the current version.'
        );
      })
      .catch(function (error) {
        beacon(String(error && error.message ? error.message : error));
      });
  };

  Assessment.prototype.renderExit = function () {
    var out = frag();
    var heading = el('h2', 'va-headline', this.copy.minorHeadline);
    heading.setAttribute('data-va-heading', '');
    out.appendChild(heading);
    out.appendChild(el('p', 'va-subline', this.copy.minorBody));

    if (this.settings.consultationUrl) {
      var link = el('button', 'va-button va-button--primary', this.copy.consultButton);
      link.type = 'button';
      link.setAttribute('data-va-action', 'consult');
      out.appendChild(link);
    }

    out.appendChild(el('p', 'va-disclaimer', this.copy.resultDisclaimer));
    return out;
  };

  /* --- result ------------------------------------------------------- */

  Assessment.prototype.renderResult = function () {
    var band = this.result.band;
    var content = this.module.bands[band.id];
    var flagged = this.flags.length > 0;

    // Two columns on desktop, one on mobile. Reading order is the same either
    // way, so the mobile stack is unaffected by the split.
    var wrap = el('div', 'va-result');
    var main = el('div', 'va-result-main');
    var side = el('div', 'va-result-side');

    var greeting = el('div', 'va-greeting');
    var hello = el(
      'h2',
      'va-greeting-name',
      this.copy.resultGreeting.replace('{name}', this.details.name)
    );
    hello.setAttribute('data-va-heading', '');
    greeting.appendChild(hello);
    greeting.appendChild(el('p', 'va-subline', this.copy.resultSubline));
    main.appendChild(greeting);

    main.appendChild(this.renderGauge(band));
    main.appendChild(el('p', 'va-meaning', content.meaning));

    var plan = this.renderPlan(content, flagged);
    var consult = this.renderConsultation(content, flagged);

    // Red flag: consultation moves above the plan and Add to cart drops below
    // it, so it stays at the top of the first column on desktop too.
    if (flagged) {
      main.appendChild(consult);
      main.appendChild(plan);
    } else {
      main.appendChild(plan);
    }

    main.appendChild(this.renderGuidance('diet'));

    side.appendChild(this.renderGuidance('habits'));
    side.appendChild(this.renderYoga());
    if (!flagged) side.appendChild(consult);
    side.appendChild(this.renderAbout());
    side.appendChild(this.renderBottomBar());
    side.appendChild(el('p', 'va-disclaimer', this.copy.resultDisclaimer));

    wrap.appendChild(main);
    wrap.appendChild(side);
    return wrap;
  };

  /**
   * Three-segment semicircular gauge. The band word is the headline; the
   * percentage is deliberately never displayed, per the guide.
   */
  Assessment.prototype.renderGauge = function (band) {
    var R = 74;
    var CX = 90;
    var CY = 90;

    function point(angle) {
      var rad = (angle * Math.PI) / 180;
      return [CX + R * Math.cos(rad), CY - R * Math.sin(rad)];
    }

    function arc(from, to) {
      var a = point(from);
      var b = point(to);
      return 'M' + a[0].toFixed(2) + ' ' + a[1].toFixed(2) +
        ' A' + R + ' ' + R + ' 0 0 1 ' + b[0].toFixed(2) + ' ' + b[1].toFixed(2);
    }

    var needle = 180 - Math.max(0, Math.min(100, this.result.pct)) * 1.8;
    var tip = point(needle);

    var card = el('div', 'va-gauge-card');
    var svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('viewBox', '0 0 180 104');
    svg.setAttribute('class', 'va-gauge');
    svg.setAttribute('role', 'img');
    svg.setAttribute('aria-label', band.label + ' — ' + this.module.label);

    [
      [180, 121, 'va-gauge-mild'],
      [119, 61, 'va-gauge-moderate'],
      [59, 0, 'va-gauge-severe']
    ].forEach(function (segment) {
      var path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
      path.setAttribute('d', arc(segment[0], segment[1]));
      path.setAttribute('class', segment[2]);
      path.setAttribute('fill', 'none');
      path.setAttribute('stroke-width', '15');
      path.setAttribute('stroke-linecap', 'round');
      svg.appendChild(path);
    });

    var line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', CX);
    line.setAttribute('y1', CY);
    line.setAttribute('x2', tip[0].toFixed(2));
    line.setAttribute('y2', tip[1].toFixed(2));
    line.setAttribute('class', 'va-gauge-needle');
    svg.appendChild(line);

    var hub = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
    hub.setAttribute('cx', CX);
    hub.setAttribute('cy', CY);
    hub.setAttribute('r', '6');
    hub.setAttribute('class', 'va-gauge-hub');
    svg.appendChild(hub);

    card.appendChild(svg);

    var label = el('div', 'va-gauge-label');
    var name = el('p', 'va-band va-band--' + band.id, band.label);
    label.appendChild(name);
    label.appendChild(el('p', 'va-band-concern', this.module.label));
    card.appendChild(label);

    return card;
  };

  Assessment.prototype.renderPlan = function (content, flagged) {
    var self = this;
    var section = el('section', 'va-section');
    section.appendChild(el('h3', 'va-section-heading', this.copy.planHeading));

    section.appendChild(this.renderProduct(content.plan.primary, false, flagged));

    // Supporting products appear on Moderate and Severe only, which is exactly
    // where the workbook supplies them.
    content.plan.supporting.forEach(function (product) {
      section.appendChild(self.renderProduct(product, true, flagged));
    });

    if (content.planNote) {
      section.appendChild(el('p', 'va-plan-note', content.planNote));
    }

    return section;
  };

  Assessment.prototype.renderProduct = function (product, supporting, flagged) {
    var self = this;
    var mapped = (this.settings.products || {})[product.key];
    var card = el('div', 'va-product' + (supporting ? ' va-product--supporting' : ''));

    var media = el('div', 'va-product-media');
    if (mapped && mapped.image) {
      var img = el('img');
      img.src = mapped.image;
      img.alt = mapped.title || product.name;
      img.loading = 'lazy';
      media.appendChild(img);
    }
    card.appendChild(media);

    var body = el('div', 'va-product-body');

    // Display the workbook name, not the Shopify title. Store titles carry SEO
    // tails ("…for Piles Relief, Bleeding, Burning, Swelling…") that wrap to
    // several lines beside the thumbnail; the comps show the short name. The
    // full store title stays available on hover.
    var title = el('p', 'va-product-title');
    if (mapped && mapped.url) {
      var link = el('a', '', product.name);
      link.href = mapped.url;
      if (mapped.title && mapped.title !== product.name) link.title = mapped.title;
      link.addEventListener('click', function () {
        self.track('quiz_result_cta_click', { cta_type: 'product' });
      });
      title.appendChild(link);
    } else {
      title.textContent = product.name;
    }
    body.appendChild(title);

    body.appendChild(el('p', 'va-product-dose', product.dose));
    if (product.note) body.appendChild(el('p', 'va-product-note', product.note));

    if (mapped && mapped.variantId) {
      if (mapped.price) {
        body.appendChild(el('p', 'va-product-price', mapped.price));
      }
      // Guide: supporting products use the same card, visually lighter. A red
      // flag demotes every Add to cart below the consultation CTA.
      var emphasis = flagged || supporting ? 'secondary' : 'primary';
      var button = el(
        'button',
        'va-button va-button--' + emphasis + ' va-add',
        mapped.available === false ? this.copy.soldOut : this.copy.addToCart
      );
      button.type = 'button';
      button.setAttribute('data-va-action', 'add-to-cart');
      button.setAttribute('data-variant-id', mapped.variantId);
      if (mapped.available === false) button.disabled = true;
      body.appendChild(button);
    }

    card.appendChild(body);
    return card;
  };

  Assessment.prototype.addToCart = function (button) {
    var variantId = button.getAttribute('data-variant-id');
    if (!variantId || button.disabled) return;

    var original = button.textContent;
    button.disabled = true;
    button.textContent = 'Adding…';
    this.track('quiz_result_cta_click', { cta_type: 'product' });

    fetch(window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root + 'cart/add.js'
      : '/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items: [{ id: Number(variantId), quantity: 1 }] })
    })
      .then(function (response) {
        if (!response.ok) throw new Error('cart');
        return response.json();
      })
      .then(function () {
        button.textContent = 'Added ✓';
        // Themes listen for one of these to refresh the cart drawer.
        document.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        document.dispatchEvent(new CustomEvent('cart:build', { bubbles: true }));
        window.setTimeout(function () {
          button.textContent = original;
          button.disabled = false;
        }, 2400);
      })
      .catch(function () {
        button.textContent = original;
        button.disabled = false;
        window.location.href = '/cart';
      });
  };

  /**
   * Guide: each bullet carries a surface flag — screen or plan. The workbook
   * supplies three of each; the first `screenBullets` render on screen, and the
   * full set is what a WhatsApp or PDF plan would carry.
   *
   * Diet and Daily habits are separate sections, as the comps show: Diet sits
   * under the accent "Your lifestyle guidance" heading with a tint chip, while
   * Daily habits carries a gold secondary label. On desktop that lets Daily
   * habits open the second column.
   */
  Assessment.prototype.renderGuidance = function (which) {
    var limit = this.settings.screenBullets || 2;
    var habits = which === 'habits';
    var data = habits ? this.module.habits : this.module.diet;

    var section = el('section', 'va-section');
    section.appendChild(
      habits
        ? el('h3', 'va-section-heading va-section-heading--alt', this.copy.habitsLabel)
        : el('h3', 'va-section-heading', this.copy.tipsHeading)
    );

    var card = el('div', 'va-card');
    if (!habits) card.appendChild(el('span', 'va-chip', this.copy.dietLabel));

    var list = el('ul', 'va-bullets');
    data.dos.slice(0, limit).forEach(function (text) {
      var item = el('li', 'va-do');
      item.appendChild(el('span', 'va-bullet-mark', '+'));
      item.appendChild(el('span', '', text));
      list.appendChild(item);
    });
    data.donts.slice(0, limit).forEach(function (text) {
      var item = el('li', 'va-dont');
      item.appendChild(el('span', 'va-bullet-mark', '−'));
      item.appendChild(el('span', '', text));
      list.appendChild(item);
    });

    card.appendChild(list);
    section.appendChild(card);
    return section;
  };

  Assessment.prototype.renderYoga = function () {
    var self = this;
    var section = el('section', 'va-section');
    section.appendChild(el('h3', 'va-section-heading', this.copy.yogaHeading));

    var row = el('div', 'va-yoga');
    this.module.yoga.forEach(function (key) {
      var card = el('div', 'va-yoga-card');
      var media = el('div', 'va-yoga-media');
      var image = (self.settings.yoga || {})[key];
      if (image) {
        var img = el('img');
        img.src = image;
        img.alt = CONTENT.yoga[key];
        img.loading = 'lazy';
        media.appendChild(img);
      }
      card.appendChild(media);
      card.appendChild(el('p', 'va-yoga-name', CONTENT.yoga[key] || key));
      row.appendChild(card);
    });

    section.appendChild(row);
    return section;
  };

  Assessment.prototype.renderConsultation = function (content, flagged) {
    var panel = el('section', 'va-consult');
    if (this.settings.doctorImage) {
      var img = el('img', 'va-consult-image');
      img.src = this.settings.doctorImage;
      img.alt = '';
      img.loading = 'lazy';
      panel.appendChild(img);
    }

    var body = el('div', 'va-consult-body');
    body.appendChild(el('h3', 'va-consult-headline', this.copy.consultHeadline));
    body.appendChild(el('p', 'va-consult-copy', this.copy.consultBody));
    body.appendChild(el('p', 'va-consult-line', content.consultation));

    var button = el(
      'button',
      'va-button va-button--' + (flagged ? 'primary' : 'secondary'),
      this.copy.consultButton
    );
    button.type = 'button';
    button.setAttribute('data-va-action', 'consult');
    body.appendChild(button);

    panel.appendChild(body);
    return panel;
  };

  Assessment.prototype.renderAbout = function () {
    var section = el('section', 'va-section va-about');
    section.appendChild(el('h3', 'va-section-heading', this.copy.aboutHeadline));
    section.appendChild(el('p', 'va-subline', this.copy.aboutBody));

    var bar = el('div', 'va-trust');
    this.copy.trustBar.forEach(function (item) {
      var cell = el('div', 'va-trust-item');
      cell.appendChild(el('p', 'va-trust-value', item.value));
      cell.appendChild(el('p', 'va-trust-label', item.label));
      bar.appendChild(cell);
    });
    section.appendChild(bar);
    return section;
  };

  /**
   * Asks the Apps Script endpoint to render the same PDF it saves for the sheet
   * and hands it to the browser as a download. Needs a readable response, so
   * unlike submitLead there is no sendBeacon fallback — on failure the button
   * says so and can be pressed again.
   */
  Assessment.prototype.downloadReport = function (button) {
    var endpoint = (this.settings.sheetEndpoint || '').trim();
    if (!endpoint || !this.lead || button.disabled) return;

    var original = this.copy.reportButton;
    button.disabled = true;
    button.textContent = 'Preparing report…';
    this.track('quiz_result_cta_click', { cta_type: 'report' });

    var envelope = Object.assign({}, this.lead, { action: 'report' });
    if (this.settings.sheetSecret) envelope.secret = this.settings.sheetSecret;

    fetch(endpoint, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(envelope)
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (result) {
        if (!result || !result.ok || !result.pdf) {
          throw new Error((result && result.error) || 'no pdf in reply');
        }

        var binary = window.atob(result.pdf);
        var bytes = new Uint8Array(binary.length);
        for (var i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        var url = URL.createObjectURL(new Blob([bytes], { type: 'application/pdf' }));

        var link = el('a');
        link.href = url;
        link.download = result.filename || 'Vedistry health assessment.pdf';
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.setTimeout(function () {
          URL.revokeObjectURL(url);
        }, 10000);

        button.textContent = original;
        button.disabled = false;
      })
      .catch(function (error) {
        console.warn('[vedistry] report download failed:', error && error.message);
        button.textContent = 'Could not create report — try again';
        button.disabled = false;
      });
  };

  Assessment.prototype.renderBottomBar = function () {
    var bar = el('div', 'va-bottom-bar');

    // Only offered when the sheet endpoint is set — it is what renders the PDF.
    if ((this.settings.sheetEndpoint || '').trim()) {
      var report = el(
        'button',
        'va-button va-button--secondary va-bottom-bar-wide',
        this.copy.reportButton
      );
      report.type = 'button';
      report.setAttribute('data-va-action', 'report');
      bar.appendChild(report);
    }

    var share = el('button', 'va-button va-button--secondary', this.copy.shareButton);
    share.type = 'button';
    share.setAttribute('data-va-action', 'share');
    bar.appendChild(share);

    var retake = el('button', 'va-button va-button--primary', this.copy.retakeButton);
    retake.type = 'button';
    retake.setAttribute('data-va-action', 'retake');
    bar.appendChild(retake);

    return bar;
  };

  /* ------------------------------------------------------------------ *
   * Boot
   * ------------------------------------------------------------------ */

  function init() {
    document.querySelectorAll('[data-vedistry-assessment]').forEach(function (root) {
      if (root.dataset.vaReady) return;
      root.dataset.vaReady = 'true';
      new Assessment(root);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);

  /**
   * Run `VedistryAssessment.testSheet()` in the browser console on the
   * assessment page. It reports the build and the endpoint it found, then posts
   * one clearly-labelled test row — so the transport can be checked without
   * filling the form, and the request shows up in the Network tab.
   */
  function testSheet() {
    var root = document.querySelector('[data-vedistry-assessment]');
    if (!root) {
      console.error('[vedistry] no assessment section on this page');
      return;
    }

    var settings = {};
    var node = root.querySelector('[data-va-settings]');
    try {
      settings = JSON.parse(node.textContent) || {};
    } catch (error) {
      console.error('[vedistry] settings JSON is malformed', error);
      return;
    }

    var endpoint = (settings.sheetEndpoint || '').trim();
    console.info('[vedistry] build   :', BUILD);
    console.info('[vedistry] endpoint:', endpoint || '(EMPTY)');
    console.info('[vedistry] secret  :', settings.sheetSecret ? 'set' : '(EMPTY)');

    if (!endpoint) {
      console.error(
        '[vedistry] No endpoint in the page. The "Apps Script Web App URL" ' +
          'setting is empty on the theme you are viewing — check you saved it ' +
          'on the PUBLISHED theme, not a draft.'
      );
      return;
    }

    return fetch(endpoint, {
      method: 'POST',
      mode: 'cors',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify({
        secret: settings.sheetSecret || '',
        submission_id: 'console-' + Date.now(),
        submitted_at: new Date().toISOString(),
        name: 'CONSOLE TEST — delete me',
        age: 30,
        mobile: '9999999999',
        consent: true,
        concern: 'Sleep',
        module: 'slp',
        module_label: 'Sleep',
        total: 9,
        max: 15,
        percentage: 60,
        band: 'Moderate',
        flagged: false,
        answers: [],
        meaning: 'Console test row.',
        page_url: window.location.href,
        version: CONTENT.version
      })
    })
      .then(function (response) {
        return response.json();
      })
      .then(function (result) {
        console.info('[vedistry] the sheet replied:', result);
        return result;
      })
      .catch(function (error) {
        console.warn(
          '[vedistry] could not read the reply (' + error.message + '). ' +
            'The row may still have been written — check the sheet.'
        );
      });
  }

  /**
   * `VedistryAssessment.products()` in the console prints every product the
   * workbook recommends and whether it resolved to a real Shopify product —
   * so you can see at a glance which cards will show Add to cart.
   */
  function products() {
    var root = document.querySelector('[data-vedistry-assessment]');
    if (!root) {
      console.error('[vedistry] no assessment section on this page');
      return;
    }

    var settings = {};
    try {
      settings = JSON.parse(root.querySelector('[data-va-settings]').textContent) || {};
    } catch (error) {
      console.error('[vedistry] settings JSON is invalid — fix that first', error);
      return;
    }

    var mapped = settings.products || {};
    var wanted = {};

    // Walk the content so the list is every product actually recommended,
    // not just the ones that happen to be mapped.
    Object.keys(CONTENT.modules).forEach(function (id) {
      var module = CONTENT.modules[id];
      ['mild', 'moderate', 'severe'].forEach(function (band) {
        var plan = module.bands[band].plan;
        [plan.primary].concat(plan.supporting).forEach(function (product) {
          wanted[product.key] = product.name;
        });
      });
    });

    var rows = Object.keys(wanted)
      .sort()
      .map(function (key) {
        var hit = mapped[key];
        return {
          key: key,
          'workbook name': wanted[key],
          'resolved to': hit ? hit.title : '— NOT MAPPED —',
          'add to cart': hit && hit.variantId ? 'yes' : 'NO',
          via: hit ? hit.source || 'block' : ''
        };
      });

    var missing = rows.filter(function (row) {
      return row['add to cart'] === 'NO';
    });

    if (console.table) console.table(rows);
    console.info(
      '[vedistry] ' + (rows.length - missing.length) + ' of ' + rows.length +
        ' products resolved.' +
        (missing.length
          ? ' Missing: ' + missing.map(function (r) { return r.key; }).join(', ')
          : ' All cards will show Add to cart.')
    );
    return rows;
  }

  window.VedistryAssessment = {
    build: BUILD,
    init: init,
    score: score,
    bandFor: bandFor,
    testSheet: testSheet,
    products: products
  };
})();
